import React, {useCallback, useEffect, useState} from 'react'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import PropTypes from 'prop-types'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

/**
 * Higher-order component that modifies the given component's `onClick` to show the login form if
 * the user is not logged in.
 * @param {React.Component} component Component to wrap.
 * @param {(location: Location, locale: string) => boolean} [options.isLoginPage] Function that
 * determines whether the current page is the "login" page, to avoid showing a duplicate modal.
 * Defaults to all paths ending with "/login".
 * @param {(location: Location, locale: string) => boolean} [options.isRegistrationPage] Function that
 * determines whether the current page is the "registration" page, to avoid showing a duplicate modal.
 * Defaults to all paths ending with "/registration".
 * @param {boolean} [options.isWishlistAction] Flag to indicate this is a wishlist action, which will
 * show the wishlist message in the auth modal.
 * @returns {React.Component} wrapped component
 */
const withRegistration = (
    Component,
    {
        isLoginPage = (loc) => loc.pathname.endsWith('/login'),
        isRegistrationPage = (loc) => loc.pathname.endsWith('/registration'),
        isWishlistAction = false
    } = {}
) => {
    const WrappedComponent = ({onClick = noop, ...passThroughProps}) => {
        const {data: customer} = useCurrentCustomer()
        const authModal = useAuthModal()
        const {showCustomToast} = useCustomToast()
        const location = useLocation()
        const {formatMessage, locale} = useIntl()
        const [pendingAction, setPendingAction] = useState(false)
        const wishlistProductId =
            passThroughProps?.wishlistProductId ||
            passThroughProps?.['data-wishlist-product-id']
        const pendingWishlistKey = 'pendingWishlistProductId'

        const runPendingAction = useCallback(() => {
            if (!pendingAction) return
            setPendingAction(false)
            if (isWishlistAction && wishlistProductId && typeof window !== 'undefined') {
                window.sessionStorage.removeItem(pendingWishlistKey)
            }
            onClick()
        }, [pendingAction, onClick, isWishlistAction, wishlistProductId])

        useEffect(() => {
            if (!customer?.isRegistered || !isWishlistAction || !wishlistProductId) return
            if (typeof window === 'undefined') return
            const pendingId = window.sessionStorage.getItem(pendingWishlistKey)
            if (pendingId && pendingId === String(wishlistProductId)) {
                window.sessionStorage.removeItem(pendingWishlistKey)
                onClick()
            }
        }, [customer?.isRegistered, isWishlistAction, wishlistProductId, onClick])

        useEffect(() => {
            if (pendingAction && customer?.isRegistered) {
                runPendingAction()
            }
        }, [pendingAction, customer?.isRegistered, runPendingAction])

        const handleClick = (e) => {
            e.preventDefault()

            if (!customer?.isRegistered) {
                if (isWishlistAction && wishlistProductId && typeof window !== 'undefined') {
                    window.sessionStorage.setItem(
                        pendingWishlistKey,
                        String(wishlistProductId)
                    )
                }
                setPendingAction(true)
                if (isLoginPage(location, locale)) {
                    showCustomToast({
                        message: formatMessage({
                            defaultMessage: 'Please sign in to continue!',
                            id: 'with_registration.info.please_sign_in'
                        })
                    })
                } else if (isRegistrationPage(location, locale)) {
                    showCustomToast({
                        message: formatMessage({
                            defaultMessage: 'You are already on the registration page.',
                            id: 'with_registration.info.already_on_registration'
                        })
                    })
                } else {
                    // Set the wishlist flag if this is a wishlist action
                    if (isWishlistAction) {
                        authModal.setIsWishlistClick(true)
                    }
                    authModal.onOpen()
                }
            } else {
                onClick()
            }
        }

        return (
            <React.Fragment>
                <Component {...passThroughProps} onClick={handleClick} />
                <AuthModal
                    {...authModal}
                    onLoginSuccess={runPendingAction}
                    onRegistrationSuccess={runPendingAction}
                />
            </React.Fragment>
        )
    }

    WrappedComponent.propTypes = {
        onClick: PropTypes.func
    }

    return WrappedComponent
}

export default withRegistration
